import React, { useState, useEffect, useRef } from "react";
import { Row, Col, ButtonGroup, Button, Form } from "react-bootstrap";
import {
  useAddIdentMutation,
  useGetAllIdentsQuery,
  useUpdateIdentMutation,
} from "../slices/identsApiSlice";
import { toast } from "react-toastify";

const Idents = () => {
  // State variables
  const [selectedIdent, setSelectedIdent] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    code: "",
    seconds: "",
  });
  const [formChanged, setFormChanged] = useState(false);

  // Fetching idents data
  const {
    data: idents,
    error: identsError,
    isLoading: identsLoading,
  } = useGetAllIdentsQuery();

  // Mutation hooks
  const [addIdent] = useAddIdentMutation();
  const [updateIdent] = useUpdateIdentMutation();

  // Ref for previous idents
  const prevIdents = useRef(idents || []);

  // Effects to handle selected ident changes and updates
  useEffect(() => {
    if (
      idents &&
      idents.length > 0 &&
      (!prevIdents.current.length || idents.length > prevIdents.current.length)
    ) {
      const newIdent = idents.find(
        (ident) =>
          !prevIdents.current.some((prevIdent) => prevIdent._id === ident._id)
      );
      if (newIdent) setSelectedIdent(newIdent._id);
    }
    prevIdents.current = idents || [];
  }, [idents, selectedIdent]);

  useEffect(() => {
    if (selectedIdent !== null) {
      const selectedIdentData = idents.find(
        (ident) => ident._id === selectedIdent
      );
      if (selectedIdentData) {
        setFormData({
          id: selectedIdent,
          name: selectedIdentData.name,
          code: selectedIdentData.code,
          seconds: selectedIdentData.seconds,
        });
      }
    }
  }, [selectedIdent, idents]);

  // Event handlers
  const handleButtonClick = (identId) => {
    setSelectedIdent(identId);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name || !formData.code || !formData.seconds) {
      toast.error("All fields are required. Document not saved.", {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    if (formData.id !== null) {
      await updateIdent(formData);
    } else {
      const newIdent = await addIdent(formData);
      setSelectedIdent(newIdent._id);
    }
    if (formData.id === null) {
      setFormData({ id: null, name: "", code: "", seconds: "" });
    }
    setFormChanged(false);
  };

  const handleAddRecord = () => {
    setFormData({ id: null, name: "", code: "", seconds: "" });
    setFormChanged(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setFormChanged(true);
  };

  // Rendering
  return (
    <Row className="mt-4">
      {/* Left Column for displaying existing idents */}
      <Col
        xs="3"
        style={{ height: "1000px", overflowY: "scroll" }}
        className="p-2 border border-1"
      >
        {identsError ? (
          <div>Error loading idents</div>
        ) : identsLoading ? (
          <div>Loading idents...</div>
        ) : (
          <ButtonGroup
            vertical
            size="sm"
            className="col-12"
          >
            {idents &&
              idents.map((ident) => (
                <Button
                  key={ident._id}
                  variant={selectedIdent === ident._id ? "primary" : "light"}
                  onClick={() => handleButtonClick(ident._id)}
                >
                  <div className="text-capitalize text-start">{ident.name}</div>
                </Button>
              ))}
          </ButtonGroup>
        )}
      </Col>
      {/* Right Column for adding/updating ident */}
      <Col className="p-2 border border-1 border-start-0">
        <Row>
          <Col>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col>
                  {/* Form Header */}
                  <Row className="h4 border-bottom pb-3 ">
                    <Col className="text-center">
                      {selectedIdent !== null ? "Update Ident" : "Add Ident"}
                    </Col>
                  </Row>
                  {/* Form Inputs */}
                  {["name", "code", "seconds"].map((field, index) => (
                    <Row
                      key={index}
                      className="justify-content-center"
                    >
                      <Col
                        sm={2}
                        className="text-end mt-2 "
                      >
                        {field.charAt(0).toUpperCase() + field.slice(1)}:
                      </Col>
                      <Col>
                        <Form.Group
                          controlId={field}
                          className="mb-3"
                        >
                          <Form.Control
                            type={field === "seconds" ? "number" : "text"}
                            name={field}
                            placeholder={`Enter ${field}`}
                            value={formData[field]}
                            onChange={handleInputChange}
                            max={field === "seconds" ? 9999 : undefined}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>
              {/* Form Buttons */}
              <Row className="mt-3 ">
                <Col className="text-end mt-3 ">
                  {/* Submit Button */}
                  <Button
                    variant="danger"
                    type="submit"
                    className="ms-3"
                    disabled={!formChanged}
                  >
                    Submit
                  </Button>
                  {/* Add New Button */}
                  <Button
                    variant="success"
                    className="ms-3 me-5 "
                    onClick={handleAddRecord}
                    disabled={formChanged}
                  >
                    Add New
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default Idents;
